import { TFunction, useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { Form, Input, Modal } from 'antd';
import { AppDispatch } from 'redux/store';
import { useDispatch } from 'react-redux';
import { useEffect, useMemo, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import FormItem from 'components/form/FormItem';
import { generateRedemptionCodes } from 'redux/reducers/redemptionCodeSlice';
import OmniNotification from 'components/OmniNotification';
import { mapErrorToErrorMessage } from 'utils/errorUtils';

const getValidationSchema = (t: TFunction) =>
	yup.object().shape({
		count: yup
			.number()
			.required(
				t(
					'redemptionCodesPage.CreateRedemptionCodeModal.CountValidation.Required',
				),
			)
			.typeError(t('validation.integer'))
			.min(
				1,
				t('redemptionCodesPage.CreateRedemptionCodeModal.CountValidation.Min'),
			)
			.max(
				1000,
				t('redemptionCodesPage.CreateRedemptionCodeModal.CountValidation.Max', {
					num: 1000,
				}),
			),
	});

interface Props {
	visible: boolean;
	setVisible: (visible: boolean) => void;
	afterSubmit: () => void;
	projectId: number;
}

interface FormValues {
	count: number;
}

export default function CreateModal(props: Props) {
	const { visible, setVisible } = props;
	const { t } = useTranslation();
	const dispatch: AppDispatch = useDispatch();
	const [modalLoading, setModalLoading] = useState(false);
	const validationSchema = useMemo(() => getValidationSchema(t), [t]);
	const { control, handleSubmit, formState, reset } = useForm<FormValues>({
		mode: 'all',
		resolver: yupResolver(validationSchema),
	});

	useEffect(() => reset(), [visible]);

	const submitInvitation = async (values: FormValues) => {
		setModalLoading(true);
		const result = await dispatch(
			generateRedemptionCodes({
				projectId: props.projectId,
				number: values.count,
			}),
		);

		if (generateRedemptionCodes.fulfilled.match(result)) {
			OmniNotification.success({
				message: t(
					'redemptionCodesPage.CreateRedemptionCodeModal.Success.Header',
				),
				description: t(
					'redemptionCodesPage.CreateRedemptionCodeModal.Success.Body',
					{
						num: values.count,
					},
				),
			});
		} else {
			OmniNotification.error({
				message: t('notification.error.title'),
				description:
					mapErrorToErrorMessage(result.payload) || t('api.defaultError'),
			});
		}
		setModalLoading(false);
		props.afterSubmit();
	};

	return (
		<>
			{visible && (
				<Modal
					title={
						<span>
							{t('redemptionCodesPage.CreateRedemptionCodeModal.Header')}
						</span>
					}
					visible
					centered
					okText={t('redemptionCodesPage.CreateRedemptionCodeModal.OkButton')}
					cancelText={t('common.cancel')}
					onOk={handleSubmit(submitInvitation)}
					onCancel={() => setVisible(false)}
					cancelButtonProps={{
						className: 'color-dark',
					}}
					okButtonProps={
						{
							type: 'primary',
							htmlType: 'submit',
							disabled: !formState.isValid,
							'data-testid': 'redemptionCode-create-modal-submit-button',
						} as any
					}
					confirmLoading={modalLoading}
					maskClosable={false}
					keyboard={false}
				>
					<>
						<Form data-testid="form" layout="vertical">
							<FormItem
								name="count"
								control={control}
								label={t(
									'redemptionCodesPage.CreateRedemptionCodeModal.Count.hint',
								)}
								render={({ field }) => (
									<Input
										{...field}
										type={'number'}
										placeholder="1-1000"
										min={1}
										max={1000}
										data-testid="count-input"
									/>
								)}
							/>
						</Form>
					</>
				</Modal>
			)}
		</>
	);
}
